// DS2 Menu: the ARIA menu pattern (↑ ↓ Home End move between the items; name it with aria-label). AccountMenu: the
// box (minWidth, width); MenuHeader: the title (children) and subtitle, nothing when both are empty; Divider; MenuItem:
// icon, the label (children), a button or a link (href + target, an "opens in a new tab" icon for _blank; onNavigate takes the other links, for
// SPA routers), variant default | danger, disabled. An item's click does not reach the parents (e.g. a table row).
import cx from "classnames";
import type {
  AnchorHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  KeyboardEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from "react";
import useI18n from "../../hooks/useTranslations";
import { ExternalLinkIcon } from "./icons";
import styles from "./Menu.module.css";

export type MenuHeaderProps = PropsWithChildren<{ subtitle?: string; className?: string }>;

export function MenuHeader({ children, subtitle, className }: MenuHeaderProps) {
  if (!children && !subtitle) return null;
  return (
    <div className={cx(styles.header, className)}>
      {children ? <span className={styles.headerTitle}>{children}</span> : null}
      {subtitle ? <span className={styles.headerSubtitle}>{subtitle}</span> : null}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cx(styles.divider, className)} />;
}

export type MenuItemProps = PropsWithChildren<{
  icon?: ReactNode;
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  onClick?: () => void;
  /** a link without target _blank calls it instead of the browser's navigation */
  onNavigate?: (href: string) => void;
  variant?: "default" | "danger";
  openInNewLabel?: string;
  disabled?: boolean;
  className?: string;
}>;

export function MenuItem({
  icon,
  children,
  href,
  target,
  onClick,
  onNavigate,
  variant = "default",
  openInNewLabel,
  disabled = false,
  className,
}: MenuItemProps) {
  const t = useI18n().translation.designSystem;
  const cls = cx(styles.item, variant === "danger" && styles.danger, className);
  const inner = (
    <>
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
      {href && target === "_blank" ? (
        <span className={styles.openInNew} role="img" aria-label={openInNewLabel ?? t.openInNewLabel}>
          <ExternalLinkIcon />
        </span>
      ) : null}
    </>
  );
  if (href) {
    // disabled: still a link, without href and out of the tab order
    return (
      <a
        role="menuitem"
        className={cls}
        href={disabled ? undefined : href}
        target={target}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          e.stopPropagation();
          onClick?.();
          if (target !== "_blank" && onNavigate) {
            e.preventDefault();
            onNavigate(href);
          }
        }}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      className={cls}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {inner}
    </button>
  );
}

type MenuChild =
  | ReactElement<MenuHeaderProps, typeof MenuHeader>
  | ReactElement<Record<string, never>, typeof Divider>
  | ReactElement<MenuItemProps, typeof MenuItem>;

export type AccountMenuProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  /** e.g. '240px': the menu's least width (default the width, else 200px) */
  minWidth?: string;
  /** e.g. '320px' (default: the content's) */
  width?: string;
  children?: MenuChild | MenuChild[];
};

export function AccountMenu({ minWidth, width, className, style, children, ...rest }: AccountMenuProps) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [
      ...e.currentTarget.querySelectorAll<HTMLElement>("[role=menuitem]:not(:disabled):not([aria-disabled=true])"),
    ];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const to =
      e.key === "ArrowDown"
        ? (at + 1) % items.length
        : e.key === "ArrowUp"
          ? (at - 1 + items.length) % items.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? items.length - 1
              : -1;
    if (to < 0) return;
    e.preventDefault();
    items[to]?.focus();
  };
  const sizes = { "--menu-min-width": minWidth, "--menu-width": width } as CSSProperties;
  return (
    <div {...rest} className={cx(styles.menu, className)} style={{ ...sizes, ...style }} role="menu" onKeyDown={onKey}>
      {children}
    </div>
  );
}

export default AccountMenu;
